<!-- ===== Header ===== -->
<header class="kk-header">
  <div class="kk-avatar">{{ customerInitials }}</div>
  <div class="kk-identity">
    <h2 class="kk-name">{{ data.name || 'Unknown customer' }}</h2>
    <button type="button" class="kk-parent-link" *ngIf="parent" (click)="openCard(parent.kundekortId)">
      <mat-icon>subdirectory_arrow_right</mat-icon> Part of {{ parent.name }}
    </button>
    <div class="kk-meta">
      <span>{{ data.customerType || 'Customer' }}</span>
      <span *ngIf="data.category">{{ data.category }}</span>
      <span *ngIf="data.orgId">Org. {{ data.orgId }}</span>
      <span *ngIf="data.powerOfficeCustomerNo">Kundenr {{ data.powerOfficeCustomerNo }}</span>
      <span *ngIf="data.ownerName">Owner {{ data.ownerName }}</span>
      <span *ngIf="units.length">{{ units.length }} unit{{ units.length > 1 ? 's' : '' }}</span>
    </div>
  </div>
  <button mat-icon-button class="kk-close" aria-label="Close" (click)="onNoClick()">
    <mat-icon>close</mat-icon>
  </button>
</header>

<!-- ===== Lease alerts ===== -->
<div class="kk-lease-banner" *ngIf="leaseAlerts.length" [class.kk-lease-banner--ended]="leaseAlerts[0].lease.state === 'ended'" role="status">
  <mat-icon>event_busy</mat-icon>
  <div class="kk-lease-banner-text">
    <div *ngFor="let s of leaseAlerts">
      <strong>#{{ s.number }} {{ s.name }}</strong>: {{ leaseLabel(s) }} ({{ formatDate(s.dates.leaseEnd) }})
    </div>
  </div>
</div>

<!-- ===== KPI strip ===== -->
<div class="kk-kpis">
  <div class="kk-kpi">
    <span class="kk-kpi-label">Systems</span>
    <span class="kk-kpi-value">{{ connectedSystems.length }}</span>
    <span class="kk-kpi-sub" *ngIf="connectedSystems.length">{{ activeSystemCount }} active</span>
  </div>
  <div class="kk-kpi">
    <span class="kk-kpi-label">Groups</span>
    <span class="kk-kpi-value">{{ groups.length }}</span>
    <span class="kk-kpi-sub" *ngIf="groups.length">{{ totalMembers }} members</span>
  </div>
  <div class="kk-kpi">
    <span class="kk-kpi-label">Contracts / month</span>
    <span class="kk-kpi-value">{{ avtaleMonthlyTotal | number:'1.0-0' }} kr</span>
    <span class="kk-kpi-sub">+ {{ driftFeeMonthly | number:'1.0-0' }} kr drift</span>
  </div>
  <div class="kk-kpi">
    <span class="kk-kpi-label">Last reconciled</span>
    <ng-container *ngIf="lastBilled; else noBilled">
      <span class="kk-kpi-value">{{ lastBilled.total | number:'1.0-0' }} kr</span>
      <span class="kk-kpi-sub">{{ monthLabel(lastBilled.month) }}</span>
    </ng-container>
    <ng-template #noBilled><span class="kk-kpi-value kk-muted">—</span></ng-template>
  </div>
  <div class="kk-kpi">
    <span class="kk-kpi-label">Open tasks</span>
    <span class="kk-kpi-value">{{ openTasks.length }}</span>
    <span class="kk-kpi-sub kk-text-risk" *ngIf="openTasks.length && openTasks[0].overdue">overdue</span>
  </div>
</div>

<div class="kk-body">

  <!-- ===================== LEFT: details ===================== -->
  <aside class="kk-side">

    <!-- Read view -->
    <section class="kk-panel" *ngIf="!editingDetails">
      <div class="kk-panel-head">
        <h3>Details</h3>
        <button mat-stroked-button class="kk-btn-sm" (click)="startEditDetails()">
          <mat-icon>edit</mat-icon> Edit
        </button>
      </div>
      <dl class="kk-dl">
        <dt>Legal name</dt><dd>{{ data.legalName || '—' }}</dd>
        <dt>Org. no.</dt><dd>{{ data.orgId || '—' }}</dd>
        <dt>Email</dt><dd><a *ngIf="data.email; else dash" [href]="'mailto:' + data.email">{{ data.email }}</a></dd>
        <dt>Phone</dt><dd><a *ngIf="data.phone; else dash" [href]="'tel:' + data.phone">{{ data.phone }}</a></dd>
        <dt>Website</dt><dd>{{ data.web || '—' }}</dd>
        <dt>Address</dt><dd>{{ addressLine || '—' }}</dd>
        <dt>Lead source</dt><dd>{{ data.leadSource || '—' }}</dd>
      </dl>

      <h4 class="kk-subhead">Organisation</h4>
      <dl class="kk-dl">
        <dt>Part of</dt>
        <dd>
          <ng-container *ngIf="parent; else noParent">
            <a href="" (click)="$event.preventDefault(); openCard(parent.kundekortId)">{{ parent.name }}</a>
            <button mat-icon-button class="kk-icon-xs" aria-label="Remove from parent" matTooltip="Remove from parent" (click)="removeParent()">
              <mat-icon>close</mat-icon>
            </button>
          </ng-container>
          <ng-template #noParent>
            <span *ngIf="units.length">— (top level, {{ units.length }} unit{{ units.length > 1 ? 's' : '' }})</span>
            <a href="" *ngIf="!units.length && !choosingParent" (click)="$event.preventDefault(); choosingParent = true">Set parent…</a>
          </ng-template>
        </dd>
      </dl>
      <div class="kk-org-suggest" *ngIf="parentSuggestions.length && !choosingParent">
        <div *ngFor="let sg of parentSuggestions" class="kk-org-suggest-row">
          <div class="kk-org-suggest-text">
            <span class="kk-strong">{{ sg.name }}</span>
            <span class="kk-small kk-muted">{{ sg.reason }}</span>
          </div>
          <button mat-stroked-button class="kk-btn-xs" [disabled]="savingOrg" (click)="setParent(sg.id)">Set as parent</button>
        </div>
      </div>
      <div class="kk-org-pick" *ngIf="choosingParent">
        <select class="custom-select custom-select-sm" [(ngModel)]="selectedParentId" aria-label="Parent organisation">
          <option [ngValue]="null" disabled>Choose parent card…</option>
          <option *ngFor="let c of parentOptions" [ngValue]="c.kundekortId">{{ c.name }}{{ c.orgId ? ' · ' + c.orgId : '' }}</option>
        </select>
        <button mat-flat-button color="primary" class="kk-btn-sm" [disabled]="!selectedParentId || savingOrg" (click)="setParent(selectedParentId)">Set</button>
        <button mat-button class="kk-btn-sm" (click)="choosingParent = false">Cancel</button>
      </div>

      <h4 class="kk-subhead">Invoicing</h4>
      <dl class="kk-dl">
        <dt>Kundenr</dt>
        <dd>
          <span *ngIf="data.powerOfficeCustomerNo; else missingKundenr">{{ data.powerOfficeCustomerNo }}</span>
          <span *ngIf="data.powerOfficeCustomerNo && data.invoiceToParent && parent" class="kk-muted"> · invoiced to {{ parent.name }}</span>
          <ng-template #missingKundenr><span class="kk-text-risk">Missing — invoices can't be sent</span></ng-template>

          <div class="kk-po" *ngIf="isSuperAdmin">
            <!-- No Kundenr: a sub-unit is invoiced to its parent by default -->
            <ng-container *ngIf="!data.powerOfficeCustomerNo">
              <button *ngIf="parent" mat-flat-button color="primary" class="kk-btn-sm" [disabled]="poBusy" (click)="invoiceToParent()">
                <mat-icon>account_tree</mat-icon> Invoice to {{ parent.name }}
              </button>
              <button *ngIf="!po" mat-stroked-button class="kk-btn-sm" [disabled]="poBusy" (click)="checkPowerOffice()">
                <mat-icon>cloud_sync</mat-icon> {{ parent ? 'Own customer in PowerOffice…' : 'Create in PowerOffice…' }}
              </button>
            </ng-container>
            <button *ngIf="data.powerOfficeCustomerNo && !data.invoiceToParent" mat-stroked-button class="kk-btn-sm" [disabled]="poBusy" (click)="updatePowerOffice()"
                    matTooltip="Push name, org. number, invoice email and address to the PowerOffice customer">
              <mat-icon>sync</mat-icon> Update PowerOffice
            </button>
            <mat-spinner *ngIf="poBusy" diameter="16"></mat-spinner>
          </div>

          <!-- Preview of what "create" would do -->
          <div class="kk-po-preview" *ngIf="po && !data.powerOfficeCustomerNo">
            <span class="kk-chip" [class.kk-chip--warn]="po.environment !== 'production'">PowerOffice {{ po.environment | uppercase }}</span>
            <ng-container *ngIf="po.customer; else poError">
              <span *ngIf="po.customer.action === 'create'">Will create <strong>{{ po.customer.name }}</strong> ({{ po.vatRate * 100 }}% VAT, invoice email {{ po.email || 'none — sent by post' }}).</span>
              <span *ngIf="po.customer.action !== 'create'">Found existing customer <strong>{{ po.customer.number }} {{ po.customer.name }}</strong>.</span>
              <button mat-flat-button color="primary" class="kk-btn-sm" [disabled]="poBusy" (click)="createInPowerOffice()">
                {{ po.customer.action === 'create' ? 'Create customer' : 'Use this customer' }}
              </button>
            </ng-container>
            <ng-template #poError><span class="kk-text-risk">{{ po.error }}</span></ng-template>
            <button mat-button class="kk-btn-sm" (click)="po = null">Cancel</button>
          </div>
        </dd>
        <dt>Invoice contact</dt>
        <dd>
          <ng-container *ngIf="invoiceContacts.length; else noInvoiceContact">
            <div *ngFor="let c of invoiceContacts" class="kk-inv-contact">
              <span class="kk-strong">{{ c.name || 'No name' }}</span>
              <a *ngIf="c.email" [href]="'mailto:' + c.email">{{ c.email }}</a>
              <a *ngIf="c.phone" [href]="'tel:' + c.phone">{{ c.phone }}</a>
            </div>
          </ng-container>
          <ng-template #noInvoiceContact>
            <span class="kk-muted">Not set · </span><a href="" (click)="$event.preventDefault(); addInvoiceContact()">Add</a>
          </ng-template>
        </dd>
        <dt>VAT</dt><dd>{{ vatLabel }}</dd>
        <dt>Our ref.</dt><dd>{{ data.ourReference || '—' }}</dd>
        <dt>Their ref.</dt><dd>{{ data.customerReference || '—' }}</dd>
        <dt>Lines</dt><dd>{{ data.invoiceLinesByGroup ? 'Per group' : 'Per system' }}</dd>
        <dt>Drift fee</dt><dd>{{ data.driftkostnaderEnabled === false ? 'Off' : '59 kr per invoice' }}</dd>
        <dt>Kickback</dt><dd>{{ data.kickbackRate != null ? ((data.kickbackRate * 100 | number:'1.0-2') + ' %') : '—' }}</dd>
        <ng-container *ngIf="data.garantiomsetning != null || data.subsidiering != null">
          <dt>Legacy</dt>
          <dd class="kk-muted">Garanti {{ data.garantiomsetning ?? '—' }} kr · Subsidy {{ data.subsidiering ?? '—' }} kr</dd>
        </ng-container>
      </dl>
    </section>

    <!-- Edit view -->
    <section class="kk-panel kk-panel--edit" *ngIf="editingDetails">
      <div class="kk-panel-head">
        <h3>Edit details</h3>
      </div>
      <div class="kk-form">
        <label class="kk-f kk-f--full">Name
          <input class="form-control form-control-sm" [(ngModel)]="edit.name">
        </label>
        <label class="kk-f kk-f--full">Legal name
          <input class="form-control form-control-sm" [(ngModel)]="edit.legalName">
        </label>
        <label class="kk-f kk-f--full">Org. number
          <div class="input-group input-group-sm">
            <input class="form-control" [(ngModel)]="edit.orgId">
            <div class="input-group-append">
              <button type="button" class="btn btn-outline-secondary" (click)="lookupOrg()"
                      [disabled]="orgLookupLoading || !edit.orgId">{{ orgLookupLoading ? '…' : 'Look up' }}</button>
            </div>
          </div>
        </label>
        <label class="kk-f">Type
          <select class="custom-select custom-select-sm" [(ngModel)]="edit.customerType">
            <option [ngValue]="null" disabled>Select…</option>
            <option *ngFor="let t of customerTypes" [ngValue]="t">{{ t }}</option>
          </select>
        </label>
        <label class="kk-f">Category
          <select class="custom-select custom-select-sm" [(ngModel)]="edit.category">
            <option [ngValue]="null" disabled>Select…</option>
            <option *ngFor="let c of categories" [ngValue]="c">{{ c }}</option>
          </select>
        </label>
        <label class="kk-f">Account owner
          <select class="custom-select custom-select-sm" [(ngModel)]="edit.ownerId">
            <option [ngValue]="null">Unassigned</option>
            <option *ngFor="let o of owners" [ngValue]="o.userId">{{ ownerLabel(o) }}</option>
          </select>
        </label>
        <label class="kk-f">Lead source
          <select class="custom-select custom-select-sm" [(ngModel)]="edit.leadSource">
            <option [ngValue]="null">— None —</option>
            <option *ngFor="let s of leadSources" [ngValue]="s">{{ s }}</option>
          </select>
        </label>
        <label class="kk-f">Email
          <input class="form-control form-control-sm" [(ngModel)]="edit.email">
        </label>
        <label class="kk-f">Phone
          <input class="form-control form-control-sm" [(ngModel)]="edit.phone">
        </label>
        <label class="kk-f kk-f--full">Website
          <input class="form-control form-control-sm" [(ngModel)]="edit.web">
        </label>
        <label class="kk-f kk-f--full">Address
          <input class="form-control form-control-sm" [(ngModel)]="edit.address">
        </label>
        <label class="kk-f kk-f--full">Address 2
          <input class="form-control form-control-sm" [(ngModel)]="edit.address2">
        </label>
        <label class="kk-f">Zip code
          <input class="form-control form-control-sm" [(ngModel)]="edit.zipCode">
        </label>
        <label class="kk-f">City
          <input class="form-control form-control-sm" [(ngModel)]="edit.city">
        </label>
        <label class="kk-f kk-f--full">Country
          <input class="form-control form-control-sm" [(ngModel)]="edit.country">
        </label>

        <h4 class="kk-subhead kk-f--full">Invoicing</h4>
        <label class="kk-f">PowerOffice Kundenr
          <input class="form-control form-control-sm" [(ngModel)]="edit.powerOfficeCustomerNo" placeholder="e.g. 30319"
                 matTooltip="Decides which PowerOffice ledger the invoices post to. Org. number is not unique, so a blank Kundenr makes the send fail rather than guess.">
        </label>
        <label class="kk-f">Invoice VAT (%)
          <input type="number" min="0" max="100" step="1" class="form-control form-control-sm" [(ngModel)]="edit.vatPercent"
                 matTooltip="15 = food MVA (default), 0 = VAT-exempt">
        </label>
        <label class="kk-f">Our reference
          <input class="form-control form-control-sm" [(ngModel)]="edit.ourReference" placeholder="Vår referanse">
        </label>
        <label class="kk-f">Customer's reference
          <input class="form-control form-control-sm" [(ngModel)]="edit.customerReference" placeholder="Deres referanse">
        </label>
        <label class="kk-f">Kickback (%)
          <input type="number" min="0" max="100" step="0.1" class="form-control form-control-sm" [(ngModel)]="edit.kickbackPercent" placeholder="None"
                 matTooltip="Garantiomsetning commission: achieved sales (inc. VAT) × 0.965 × this %">
        </label>
        <div class="kk-f kk-f--full kk-checks">
          <mat-checkbox [(ngModel)]="edit.invoiceLinesByGroup"
                        matTooltip="Subsidy, credit and pant listed per group instead of per system">Show groups on invoice</mat-checkbox>
          <mat-checkbox [(ngModel)]="edit.driftkostnaderEnabled"
                        matTooltip="Administrasjonsgebyr: 59 kr ex. VAT on every invoice for this customer">Bill driftskostnader</mat-checkbox>
        </div>
      </div>
      <div class="kk-edit-actions">
        <button mat-button (click)="cancelEditDetails()" [disabled]="savingInfo">Cancel</button>
        <button mat-flat-button color="primary" (click)="saveInfo()" [disabled]="savingInfo">
          {{ savingInfo ? 'Saving…' : 'Save' }}
        </button>
      </div>
    </section>

    <!-- Contacts -->
    <section class="kk-panel">
      <div class="kk-panel-head">
        <h3>Contacts <span class="kk-count">{{ existingContacts.length }}</span></h3>
        <button mat-icon-button class="kk-icon-sm" aria-label="Add contact" matTooltip="Add contact" (click)="addContactPerson()">
          <mat-icon>person_add</mat-icon>
        </button>
      </div>
      <div *ngIf="existingContacts.length; else noContacts" class="kk-contacts">
        <div *ngFor="let c of existingContacts" class="kk-contact">
          <div class="kk-contact-main">
            <div class="kk-contact-name">
              {{ c.name || 'No name' }}<span *ngIf="c['stilling']" class="kk-muted"> · {{ c['stilling'] }}</span>
            </div>
            <div class="kk-contact-lines">
              <a *ngIf="c.email" [href]="'mailto:' + c.email">{{ c.email }}</a>
              <a *ngIf="c.phone" [href]="'tel:' + c.phone">{{ c.phone }}</a>
            </div>
            <div class="kk-chips" *ngIf="c.roles?.length">
              <span class="kk-chip" *ngFor="let role of c.roles">{{ role }}</span>
            </div>
          </div>
          <div class="kk-row-actions">
            <button mat-icon-button class="kk-icon-sm" aria-label="Edit contact" (click)="editContactPerson(c)"><mat-icon>edit</mat-icon></button>
            <button mat-icon-button class="kk-icon-sm" aria-label="Remove from customer" matTooltip="Remove from this customer" (click)="removeExistingContact(c)"><mat-icon>link_off</mat-icon></button>
          </div>
        </div>
      </div>
      <ng-template #noContacts><p class="kk-empty-line">No contact persons yet.</p></ng-template>
    </section>

    <!-- Portal access (managed on the Portal Access page) -->
    <section class="kk-panel" *ngIf="isSuperAdmin">
      <div class="kk-panel-head">
        <h3>Portal access <span class="kk-count">{{ portalUsers.length }}</span></h3>
        <a mat-icon-button class="kk-icon-sm" routerLink="/dashboard/portal-access" (click)="onNoClick()"
           aria-label="Manage portal access" matTooltip="Manage on Portal Access">
          <mat-icon>manage_accounts</mat-icon>
        </a>
      </div>
      <div *ngIf="portalUsers.length; else noPortal" class="kk-contacts">
        <div *ngFor="let u of portalUsers" class="kk-contact">
          <div class="kk-contact-main">
            <div class="kk-contact-name">{{ u.name || u.email }}
              <span class="kk-chip">{{ u.role }}</span>
              <span class="kk-chip kk-chip--risk" *ngIf="!u.status">Disabled</span>
            </div>
            <div class="kk-contact-lines kk-muted">
              <span *ngIf="portalWhen(u.lastLogin)">Last sign-in {{ portalWhen(u.lastLogin) }}</span>
              <span *ngIf="!portalWhen(u.lastLogin) && portalWhen(u.createdAt)">Invited {{ portalWhen(u.createdAt) }}</span>
            </div>
          </div>
        </div>
      </div>
      <ng-template #noPortal>
        <p class="kk-empty-line">{{ portalLoaded ? 'No one at this customer has portal access.' : 'Loading…' }}</p>
      </ng-template>
    </section>
  </aside>

  <!-- ===================== RIGHT: tabs ===================== -->
  <main class="kk-main">
    <mat-tab-group animationDuration="0ms" mat-stretch-tabs="false" mat-align-tabs="start">

      <!-- ===== SYSTEMS ===== -->
      <mat-tab>
        <ng-template mat-tab-label>Systems <span class="kk-tab-count">{{ connectedSystems.length }}</span></ng-template>
        <div class="kk-tab">
          <div class="kk-table-wrap" *ngIf="connectedSystems.length; else noSystems">
            <table class="kk-table">
              <thead>
                <tr>
                  <th class="kk-col-num">#</th>
                  <th>System</th>
                  <th>Status</th>
                  <th class="num">Last sale</th>
                  <th class="num">Garanti</th>
                  <th class="num">Subsidy</th>
                  <th class="num">Groups</th>
                  <th class="num">Contract / mo</th>
                  <th class="num">Lease ends</th>
                  <th class="kk-col-actions"></th>
                </tr>
              </thead>
              <tbody>
                <ng-container *ngFor="let s of connectedSystems">
                  <tr class="kk-row" [class.kk-row--open]="expandedSystem[s.linkId]" [ngClass]="s.lease && s.lease.state !== 'ok' ? 'kk-row--lease-' + s.lease.state : ''" (click)="toggleSystem(s)">
                    <td class="kk-col-num"><span class="kk-num">{{ s.number || '?' }}</span></td>
                    <td>
                      <div class="kk-strong">{{ s.missing ? 'System not found' : (s.name || 'Unnamed') }}</div>
                      <div class="kk-small kk-muted">
                        {{ s.siteName || '—' }}<span *ngIf="s.systemType"> · {{ s.systemType }}</span><span *ngIf="s.canteen"> · Canteen</span>
                      </div>
                    </td>
                    <td>
                      <span class="kk-badge" [ngClass]="s.active ? 'kk-badge--on' : 'kk-badge--off'">
                        <span class="kk-dot"></span>{{ s.active ? 'Active' : 'Inactive' }}
                      </span>
                      <span class="kk-badge kk-badge--warn" *ngIf="s.pay.serviceMode">Service</span>
                      <span class="kk-badge kk-badge--warn" *ngIf="s.sharedWith?.length"
                            [matTooltip]="'Also linked to: ' + s.sharedWith.join(', ')">Shared</span>
                    </td>
                    <td class="num">
                      <span *ngIf="!s.healthLoaded" class="kk-muted">…</span>
                      <ng-container *ngIf="s.healthLoaded">
                        <span *ngIf="s.daysSinceLastSale === null" class="kk-text-risk">Never</span>
                        <span *ngIf="s.daysSinceLastSale !== null" [class.kk-text-risk]="s.daysSinceLastSale > 30">
                          {{ s.daysSinceLastSale === 0 ? 'Today' : s.daysSinceLastSale + ' d ago' }}
                        </span>
                      </ng-container>
                    </td>
                    <td class="num">{{ s.garantiomsetning ? (s.garantiomsetning | number:'1.0-0') + ' kr' : '—' }}</td>
                    <td class="num">{{ s.subsidy ? (s.subsidy | number:'1.0-2') + ' kr' : '—' }}</td>
                    <td class="num">{{ s.groupNames.length || '—' }}</td>
                    <td class="num">{{ avtaleMonthlyBySystem[s.avtaleKey] ? (avtaleMonthlyBySystem[s.avtaleKey] | number:'1.0-0') + ' kr' : '—' }}</td>
                    <td class="num">
                      <span *ngIf="!s.lease" class="kk-muted">—</span>
                      <ng-container *ngIf="s.lease">
                        <div>{{ formatDate(s.dates.leaseEnd) }}</div>
                        <span *ngIf="s.lease.state !== 'ok'" class="kk-badge" [ngClass]="s.lease.state === 'ended' ? 'kk-badge--risk' : 'kk-badge--warn'">
                          {{ s.lease.state === 'ended' ? 'Ended' : s.lease.days + ' d left' }}
                        </span>
                      </ng-container>
                    </td>
                    <td class="kk-col-actions">
                      <button mat-icon-button class="kk-icon-sm" matTooltip="Edit system settings" aria-label="Edit system settings"
                              [disabled]="!s.docId" (click)="editSystem(s, $event)"><mat-icon>tune</mat-icon></button>
                      <button mat-icon-button class="kk-icon-sm" matTooltip="Disconnect from customer" aria-label="Disconnect system"
                              (click)="removeSystem(s, $event)"><mat-icon>link_off</mat-icon></button>
                      <mat-icon class="kk-chevron">{{ expandedSystem[s.linkId] ? 'expand_less' : 'expand_more' }}</mat-icon>
                    </td>
                  </tr>
                  <tr *ngIf="expandedSystem[s.linkId]" class="kk-detail-row">
                    <td colspan="10">
                      <div class="kk-detail">
                        <div class="kk-detail-col">
                          <h5>Settings
                            <button mat-button color="primary" class="kk-btn-xs" [disabled]="!s.docId" (click)="editSystem(s, $event)">
                              <mat-icon>tune</mat-icon> Edit
                            </button>
                          </h5>
                          <dl class="kk-dl kk-dl--tight">
                            <dt>Description</dt><dd>{{ s.description || '—' }}</dd>
                            <dt>Site</dt><dd>{{ s.siteName || '—' }}</dd>
                            <dt>Payments</dt>
                            <dd class="kk-flags">
                              <span [class.on]="s.pay.card">Card</span>
                              <span [class.on]="s.pay.vipps">Vipps</span>
                              <span [class.on]="s.pay.credit">Credit</span>
                              <span [class.on]="s.pay.groupSubsidy">Group subsidy</span>
                              <span [class.on]="s.pay.guest">Guest</span>
                            </dd>
                            <dt>Invoice</dt>
                            <dd>{{ s.pay.invoiceBySystem ? 'Own invoice for this system' : 'Combined with customer' }}<span *ngIf="s.customerReference"> · ref. {{ s.customerReference }}</span></dd>
                            <dt>Garanti payer</dt>
                            <dd [class.kk-text-risk]="s.garantiPayerLabel.startsWith('Not set') && s.garantiomsetning">{{ s.garantiPayerLabel }}</dd>
                            <dt *ngIf="s.sharedWith?.length">Also linked to</dt>
                            <dd *ngIf="s.sharedWith?.length">{{ s.sharedWith.join(', ') }}</dd>
                            <dt>IP</dt><dd class="kk-mono">{{ s.ip || '—' }}</dd>
                            <dt>System ID</dt><dd class="kk-mono">{{ s.docId || s.systemId }}</dd>
                          </dl>
                        </div>
                        <div class="kk-detail-col">
                          <h5>Dates
                            <button mat-button color="primary" class="kk-btn-xs" *ngIf="!datesDraft[s.linkId]" [disabled]="!s.docId" (click)="startEditDates(s, $event)">
                              <mat-icon>edit_calendar</mat-icon> Edit
                            </button>
                          </h5>
                          <dl class="kk-dl kk-dl--tight" *ngIf="!datesDraft[s.linkId]">
                            <dt>Installed</dt><dd>{{ formatDate(s.dates.install) || '—' }}</dd>
                            <dt>Kick-off</dt><dd>{{ formatDate(s.dates.kickoff) || '—' }}</dd>
                            <dt>Lease ends</dt>
                            <dd>
                              {{ formatDate(s.dates.leaseEnd) || '—' }}
                              <span *ngIf="s.lease && s.lease.state !== 'ok'" class="kk-badge" [ngClass]="s.lease.state === 'ended' ? 'kk-badge--risk' : 'kk-badge--warn'">{{ leaseLabel(s) }}</span>
                              <div class="kk-small kk-muted" *ngIf="s.leaseReminder?.taskId && s.lease?.state !== 'ended'">Reminder set {{ leaseWarnDays }} days before</div>
                            </dd>
                          </dl>
                          <div class="kk-dates-form" *ngIf="datesDraft[s.linkId] as d">
                            <label class="kk-f">Installation date (montering)
                              <input type="date" class="form-control form-control-sm" [(ngModel)]="d.install">
                            </label>
                            <label class="kk-f">Kick-off
                              <input type="date" class="form-control form-control-sm" [(ngModel)]="d.kickoff">
                            </label>
                            <label class="kk-f">Lease end
                              <input type="date" class="form-control form-control-sm" [(ngModel)]="d.leaseEnd">
                            </label>
                            <p class="kk-hint kk-f--full">A reminder task and email go to the account owner one month before the lease ends.</p>
                            <div class="kk-avtale-actions kk-f--full">
                              <button mat-flat-button color="primary" class="kk-btn-sm" [disabled]="datesSaving[s.linkId]" (click)="saveDates(s)">
                                {{ datesSaving[s.linkId] ? 'Saving…' : 'Save' }}
                              </button>
                              <button mat-button class="kk-btn-sm" (click)="cancelEditDates(s)">Cancel</button>
                            </div>
                          </div>
                          <h5 class="kk-h5-gap">Groups on this system</h5>
                          <ul class="kk-plain-list" *ngIf="s.groupNames.length; else noSysGroups">
                            <li *ngFor="let n of s.groupNames">{{ n }}</li>
                          </ul>
                          <ng-template #noSysGroups><p class="kk-empty-line">No groups of this customer are valid here.</p></ng-template>
                        </div>
                        <div class="kk-detail-col kk-detail-col--wide">
                          <h5>Contract charges</h5>
                          <ng-container *ngTemplateOutlet="avtaleBlock; context: { $implicit: s }"></ng-container>
                        </div>
                      </div>
                    </td>
                  </tr>
                </ng-container>
              </tbody>
            </table>
          </div>
          <ng-template #noSystems>
            <div class="kk-empty"><mat-icon>dns</mat-icon><p>No systems connected to this customer.</p></div>
          </ng-template>

          <div class="kk-add-row">
            <input class="form-control form-control-sm" type="text" placeholder="Search by name, site or number to connect a system…"
                   aria-label="Search system" [(ngModel)]="systemSearch" (ngModelChange)="onSystemSearch($event)"
                   [matAutocomplete]="sysAuto">
            <mat-autocomplete #sysAuto="matAutocomplete" [displayWith]="systemOptionLabel">
              <mat-option *ngFor="let o of filteredSystems" [value]="o">{{ o.label }}</mat-option>
              <mat-option *ngIf="!filteredSystems.length" disabled>No matching systems</mat-option>
            </mat-autocomplete>
            <button mat-flat-button color="primary" [disabled]="!selectedSystemId" (click)="addSystem()">
              <mat-icon>add_link</mat-icon> Connect
            </button>
          </div>
        </div>
      </mat-tab>

      <!-- ===== GROUPS ===== -->
      <mat-tab>
        <ng-template mat-tab-label>Groups <span class="kk-tab-count">{{ groups.length }}</span></ng-template>
        <div class="kk-tab">
          <div class="kk-table-wrap" *ngIf="groups.length; else noGroups">
            <table class="kk-table">
              <thead>
                <tr>
                  <th>Group</th>
                  <th>Site</th>
                  <th>Type</th>
                  <th class="num">Amount</th>
                  <th class="num">Members</th>
                  <th>Valid on</th>
                  <th>Own invoice ref.</th>
                  <th class="kk-col-actions"></th>
                </tr>
              </thead>
              <tbody>
                <tr *ngFor="let g of groups" class="kk-row">
                  <td>
                    <div class="kk-strong">{{ g.name || g.groupId }}</div>
                    <div class="kk-small kk-muted kk-mono">{{ g.groupId }}</div>
                  </td>
                  <td>{{ g.siteName }}</td>
                  <td>
                    <span class="kk-badge" [ngClass]="g.worker === 'subsidyGroup' ? 'kk-badge--cap' : 'kk-badge--on'">{{ g.typeLabel }}</span>
                    <div class="kk-small kk-muted" *ngIf="g.worker !== 'subsidyGroup'">
                      Monthly renewal<span *ngIf="g.accumulate"> · accumulates</span>
                    </div>
                  </td>
                  <td class="num">{{ g.amount | number:'1.0-2' }} kr</td>
                  <td class="num">{{ g.memberCount ?? '…' }}</td>
                  <td class="kk-small">
                    <span *ngIf="!g.systemLabels.length" class="kk-muted">—</span>
                    <div *ngFor="let l of g.systemLabels">{{ l }}</div>
                  </td>
                  <td class="kk-small">{{ g.customerReference || '—' }}</td>
                  <td class="kk-col-actions">
                    <button mat-icon-button class="kk-icon-sm" matTooltip="Edit group" aria-label="Edit group" (click)="editGroup(g)"><mat-icon>edit</mat-icon></button>
                    <button mat-icon-button class="kk-icon-sm" matTooltip="Remove from customer" aria-label="Remove group from customer" (click)="unlinkGroup(g)"><mat-icon>link_off</mat-icon></button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <ng-template #noGroups>
            <div class="kk-empty"><mat-icon>groups</mat-icon><p>No groups invoice to this customer.</p></div>
          </ng-template>

          <div class="kk-add-row">
            <select class="custom-select custom-select-sm" [(ngModel)]="selectedGroupId" aria-label="Select group">
              <option [ngValue]="null" disabled>Link an unassigned group…</option>
              <option *ngFor="let g of linkableGroups" [ngValue]="g.dbid">{{ g.label }}</option>
            </select>
            <button mat-flat-button color="primary" [disabled]="!selectedGroupId" (click)="linkGroup()">
              <mat-icon>add_link</mat-icon> Link
            </button>
          </div>
        </div>
      </mat-tab>

      <!-- ===== UNITS (organisation tree; hidden on a unit itself) ===== -->
      <mat-tab *ngIf="!parent">
        <ng-template mat-tab-label>Units <span class="kk-tab-count" *ngIf="units.length">{{ units.length }}</span></ng-template>
        <div class="kk-tab">
          <p class="kk-hint">
            Schools, departments or sites that belong to this organisation. Each unit keeps its own card, systems and invoice —
            this only groups them here and in the customer list.
          </p>
          <div class="kk-table-wrap" *ngIf="units.length; else noUnits">
            <table class="kk-table">
              <thead>
                <tr>
                  <th>Unit</th>
                  <th>Kundenr</th>
                  <th>Systems</th>
                  <th class="num">Groups</th>
                  <th class="num">Contract / mo</th>
                  <th class="num">Last reconciled</th>
                  <th class="kk-col-actions"></th>
                </tr>
              </thead>
              <tbody>
                <tr *ngFor="let u of units" class="kk-row" (click)="openCard(u.kundekortId)">
                  <td>
                    <div class="kk-strong">{{ u.name }}</div>
                    <div class="kk-small kk-muted">{{ u.orgId ? 'Org. ' + u.orgId : '' }}<span *ngIf="u.customerReference"> · ref. {{ u.customerReference }}</span></div>
                  </td>
                  <td>
                    <span *ngIf="u.powerOfficeCustomerNo; else unitNoKnr" class="kk-mono">{{ u.powerOfficeCustomerNo }}</span>
                    <ng-template #unitNoKnr><span class="kk-text-risk">Missing</span></ng-template>
                  </td>
                  <td class="kk-small">
                    <span *ngIf="!u.systemLabels.length" class="kk-muted">—</span>
                    <div *ngFor="let l of u.systemLabels">{{ l }}</div>
                  </td>
                  <td class="num">{{ u.groupCount || '—' }}</td>
                  <td class="num">{{ u.contract ? (u.contract | number:'1.0-0') + ' kr' : '—' }}</td>
                  <td class="num">
                    <ng-container *ngIf="u.lastBilled; else unitNoBill">{{ u.lastBilled.total | number:'1.0-0' }} kr <span class="kk-small kk-muted">{{ monthLabel(u.lastBilled.month) }}</span></ng-container>
                    <ng-template #unitNoBill><span class="kk-muted">—</span></ng-template>
                  </td>
                  <td class="kk-col-actions">
                    <button mat-icon-button class="kk-icon-sm" matTooltip="Remove from organisation" aria-label="Remove unit"
                            (click)="$event.stopPropagation(); unlinkUnit(u)"><mat-icon>link_off</mat-icon></button>
                    <mat-icon class="kk-chevron">chevron_right</mat-icon>
                  </td>
                </tr>
                <tr class="kk-total-row">
                  <td>Total, {{ units.length }} unit{{ units.length > 1 ? 's' : '' }}</td>
                  <td></td>
                  <td>{{ unitTotals.systems }} system{{ unitTotals.systems === 1 ? '' : 's' }}</td>
                  <td class="num">{{ unitTotals.groups }}</td>
                  <td class="num">{{ unitTotals.contract | number:'1.0-0' }} kr</td>
                  <td class="num">{{ unitTotals.lastBilled | number:'1.0-0' }} kr</td>
                  <td></td>
                </tr>
              </tbody>
            </table>
          </div>
          <ng-template #noUnits>
            <div class="kk-empty"><mat-icon>account_tree</mat-icon><p>No units under this organisation.</p></div>
          </ng-template>

          <div class="kk-add-row">
            <select class="custom-select custom-select-sm" [(ngModel)]="selectedUnitId" aria-label="Select card">
              <option [ngValue]="null" disabled>Add an existing customer card as a unit…</option>
              <option *ngFor="let c of unitOptions" [ngValue]="c.kundekortId">{{ c.name }}{{ c.orgId ? ' · ' + c.orgId : '' }}</option>
            </select>
            <button mat-flat-button color="primary" [disabled]="!selectedUnitId || savingOrg" (click)="linkUnit()">
              <mat-icon>add_link</mat-icon> Add
            </button>
          </div>

          <div class="kk-section" *ngIf="data.orgId">
            <div class="kk-section-head">
              <h3 class="kk-section-title">Registered sub-units in Brønnøysund</h3>
              <button mat-stroked-button class="kk-btn-sm" *ngIf="brregUnitsState !== 'loading'" (click)="loadBrregUnits()">
                <mat-icon>travel_explore</mat-icon> {{ brregUnitsState === 'done' ? 'Refresh' : 'Look up' }}
              </button>
            </div>
            <p class="kk-hint" *ngIf="brregUnitsState === 'idle'">Find the schools/departments registered under org. {{ data.orgId }} and add them in one click.</p>
            <p class="kk-hint" *ngIf="brregUnitsState === 'loading'">Looking up…</p>
            <p class="kk-hint kk-text-risk" *ngIf="brregUnitsState === 'error'">Could not reach the Brønnøysund register.</p>
            <p class="kk-hint" *ngIf="brregUnitsState === 'done' && !brregUnits.length">No sub-units registered under this org. number.</p>
            <div class="kk-table-wrap" *ngIf="brregUnitsState === 'done' && brregUnits.length">
              <table class="kk-table">
                <tbody>
                  <tr *ngFor="let u of brregUnits">
                    <td>
                      <div class="kk-strong">{{ u.name }}</div>
                      <div class="kk-small kk-muted">Org. {{ u.orgId }}<span *ngIf="u.city"> · {{ u.city }}</span></div>
                    </td>
                    <td class="kk-col-actions">
                      <span *ngIf="u.isUnit" class="kk-badge kk-badge--on">Unit</span>
                      <ng-container *ngIf="!u.isUnit && u.card">
                        <span *ngIf="u.card.parentKundekortId" class="kk-badge kk-badge--off">Under another card</span>
                        <button *ngIf="!u.card.parentKundekortId" mat-stroked-button class="kk-btn-xs" (click)="linkExistingUnit(u)">
                          Add "{{ u.card.name }}"
                        </button>
                      </ng-container>
                      <button *ngIf="!u.card" mat-button color="primary" class="kk-btn-xs" [disabled]="savingOrg" (click)="createUnitFromBrreg(u)">
                        <mat-icon>add</mat-icon> Create card
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </mat-tab>

      <!-- ===== BILLING ===== -->
      <mat-tab label="Billing">
        <div class="kk-tab">
          <div class="kk-section">
            <div class="kk-section-head">
              <h3 class="kk-section-title">Billing history</h3>
              <a mat-button color="primary" class="kk-btn-xs" routerLink="/dashboard/billing" (click)="onNoClick()">
                Open Billing <mat-icon>open_in_new</mat-icon>
              </a>
            </div>
            <p class="kk-hint">From saved monthly reconciliations: what this customer's systems and groups ran up, ex. contract charges and drift fee.</p>
            <div class="kk-table-wrap" *ngIf="billingHistory.length; else noHistory">
              <table class="kk-table">
                <thead>
                  <tr>
                    <th>Month</th>
                    <th class="num">Subsidy (ex. VAT)</th>
                    <th class="num">Credit</th>
                    <th class="num">Pant (credit)</th>
                    <th class="num">Billable total</th>
                    <th class="num">Flagged</th>
                    <th class="kk-col-actions"></th>
                  </tr>
                </thead>
                <tbody>
                  <ng-container *ngFor="let b of billingHistory">
                    <tr class="kk-row" (click)="toggleMonth(b)">
                      <td>
                        <span class="kk-strong">{{ monthLabel(b.month) }}</span>
                        <span class="kk-small kk-muted" *ngIf="b.company"> · {{ b.company }}</span>
                      </td>
                      <td class="num">{{ b.subsidy | number:'1.2-2' }}</td>
                      <td class="num">{{ b.credit | number:'1.2-2' }}</td>
                      <td class="num">{{ b.creditPant | number:'1.2-2' }}</td>
                      <td class="num kk-strong">{{ b.total | number:'1.2-2' }}</td>
                      <td class="num" [class.kk-text-risk]="b.flagged">{{ b.flagged || '—' }}</td>
                      <td class="kk-col-actions">
                        <mat-icon class="kk-chevron" *ngIf="b.groups.length > 1">{{ expandedMonth[b.key] ? 'expand_less' : 'expand_more' }}</mat-icon>
                      </td>
                    </tr>
                    <tr *ngIf="expandedMonth[b.key]" class="kk-detail-row">
                      <td colspan="7">
                        <table class="kk-table kk-table--nested">
                          <tbody>
                            <tr *ngFor="let g of b.groups">
                              <td>{{ g.groupName || (g.groupId ? '(unnamed group)' : 'No group') }}</td>
                              <td class="num">{{ g.confirmedSubsidyExVAT | number:'1.2-2' }}</td>
                              <td class="num">{{ g.creditConsumed | number:'1.2-2' }}</td>
                              <td class="num">{{ g.creditPantTotal | number:'1.2-2' }}</td>
                              <td class="num kk-strong">{{ g.billableTotal | number:'1.2-2' }}</td>
                            </tr>
                          </tbody>
                        </table>
                      </td>
                    </tr>
                  </ng-container>
                </tbody>
              </table>
            </div>
            <ng-template #noHistory>
              <p class="kk-empty-line">{{ billingLoaded ? 'No saved reconciliation includes this customer yet.' : 'Loading…' }}</p>
            </ng-template>
          </div>

          <div class="kk-section">
            <div class="kk-section-head">
              <h3 class="kk-section-title">Contract charges</h3>
              <span class="kk-badge kk-badge--cap">{{ avtaleMonthlyTotal | number:'1.2-2' }} kr this month ex. VAT</span>
            </div>
            <p class="kk-hint">
              Rent, driftstøtte and service per system, billed at 25% VAT. The drift fee
              ({{ data.driftkostnaderEnabled === false ? 'off for this customer' : '59 kr per invoice' }}) is added automatically.
            </p>
            <div *ngFor="let s of connectedSystems" class="kk-avtale-group">
              <div class="kk-avtale-sys">#{{ s.number }} {{ s.name }}</div>
              <ng-container *ngTemplateOutlet="avtaleBlock; context: { $implicit: s }"></ng-container>
            </div>
            <p class="kk-empty-line" *ngIf="!connectedSystems.length">Connect a system to add contract charges.</p>
          </div>

          <div class="kk-section" *ngIf="garantiElsewhere.length">
            <div class="kk-section-head">
              <h3 class="kk-section-title">Pays garanti for</h3>
            </div>
            <p class="kk-hint">Systems not connected here whose garantiomsetning shortfall is billed to this customer.</p>
            <ul class="kk-plain-list">
              <li *ngFor="let s of garantiElsewhere">#{{ s.number }} {{ s.name }} · {{ s.garantiomsetning ? (s.garantiomsetning | number:'1.0-0') + ' kr' : 'no garanti set' }}</li>
            </ul>
          </div>
        </div>
      </mat-tab>

      <!-- ===== ACTIVITY ===== -->
      <mat-tab>
        <ng-template mat-tab-label>Activity <span class="kk-tab-count" *ngIf="openTasks.length">{{ openTasks.length }}</span></ng-template>
        <div class="kk-tab">
          <div class="kk-quick-add">
            <button mat-stroked-button class="kk-btn-sm" (click)="addNote()"><mat-icon>sticky_note_2</mat-icon> Note</button>
            <button mat-stroked-button class="kk-btn-sm" (click)="addTask()"><mat-icon>add_task</mat-icon> Task</button>
            <button mat-stroked-button class="kk-btn-sm" (click)="addMoteReferat()"><mat-icon>event_note</mat-icon> Meeting note</button>
            <button mat-stroked-button class="kk-btn-sm" (click)="addDriftAvtale()"><mat-icon>fact_check</mat-icon> Service agreement</button>
          </div>

          <div class="kk-section" *ngIf="openTasks.length">
            <h3 class="kk-section-title">Open tasks</h3>
            <div *ngFor="let t of openTasks" class="kk-task">
              <button mat-icon-button class="kk-icon-sm" color="primary" aria-label="Mark complete" matTooltip="Mark complete" (click)="toggleTaskStatus(t)">
                <mat-icon>radio_button_unchecked</mat-icon>
              </button>
              <div class="kk-task-main">
                <div class="kk-strong">{{ t.title }}</div>
                <div class="kk-small" [class.kk-text-risk]="t.overdue" [class.kk-muted]="!t.overdue">
                  Due {{ t.due | date:'d MMM yyyy' }}<span *ngIf="t.assigneeName"> · {{ t.assigneeName }}</span>
                </div>
              </div>
              <button mat-icon-button class="kk-icon-sm" aria-label="Delete task" (click)="removeActivity({ type: 'task', dbid: t.dbid, title: 'Task' })">
                <mat-icon>delete_outline</mat-icon>
              </button>
            </div>
          </div>

          <div class="kk-section">
            <div class="kk-filters">
              <button *ngFor="let f of activityFilters" type="button" class="kk-filter"
                      [class.active]="activityFilter === f.key" (click)="setActivityFilter(f.key)">
                {{ f.label }} <span>{{ activityCounts[f.key] || 0 }}</span>
              </button>
            </div>
            <div *ngIf="filteredActivity.length; else noActivity" class="kk-feed">
              <div *ngFor="let a of filteredActivity" class="kk-feed-item">
                <mat-icon class="kk-feed-icon">{{ a.icon }}</mat-icon>
                <div class="kk-feed-main">
                  <div class="kk-feed-head">
                    <span class="kk-strong">{{ a.title }}</span>
                    <span class="kk-small kk-muted">{{ a.date | date:'d MMM yyyy, HH:mm' }} · {{ a.by || 'unknown' }}</span>
                  </div>
                  <div class="kk-feed-text" *ngIf="a.text">{{ a.text }}</div>
                  <div class="kk-small kk-muted" *ngIf="a.extra">{{ a.extra }}</div>
                </div>
                <button *ngIf="a.type === 'agreement' || a.type === 'meeting' || a.type === 'task'"
                        mat-icon-button class="kk-icon-sm kk-feed-del" aria-label="Delete" (click)="removeActivity(a)">
                  <mat-icon>delete_outline</mat-icon>
                </button>
              </div>
            </div>
            <ng-template #noActivity>
              <div class="kk-empty"><mat-icon>history</mat-icon><p>Nothing recorded yet.</p></div>
            </ng-template>
          </div>
        </div>
      </mat-tab>

      <!-- ===== FILES ===== -->
      <mat-tab>
        <ng-template mat-tab-label>Files <span class="kk-tab-count" *ngIf="attachments.length">{{ attachments.length }}</span></ng-template>
        <div class="kk-tab">
          <label class="kk-upload" for="fileUpload">
            <mat-icon>cloud_upload</mat-icon>
            <span>Choose file(s) to upload</span>
            <input type="file" id="fileUpload" (change)="onFileSelected($event)" multiple hidden>
          </label>
          <mat-progress-bar *ngIf="uploadProgress > 0 && uploadProgress < 100" mode="determinate" [value]="uploadProgress"></mat-progress-bar>
          <div *ngIf="attachments.length; else noAttachments">
            <div *ngFor="let att of attachments" class="kk-attach-item">
              <a [href]="att.url" target="_blank" rel="noopener"><mat-icon>description</mat-icon> {{ att.name }}</a>
              <button type="button" mat-icon-button class="kk-icon-sm" aria-label="Delete attachment" (click)="removeAttachment(att)">
                <mat-icon>delete_outline</mat-icon>
              </button>
            </div>
          </div>
          <ng-template #noAttachments><p class="kk-empty-line">No files uploaded.</p></ng-template>
        </div>
      </mat-tab>
    </mat-tab-group>
  </main>
</div>

<ng-template #dash>—</ng-template>

<!-- Contract charges for one system: used in the system detail row and the Billing tab. -->
<ng-template #avtaleBlock let-s>
  <div class="kk-avtale">
    <div class="kk-avtale-row" *ngFor="let l of avtaleLinesFor(s)" [class.kk-avtale-row--off]="l.active === false">
      <span class="kk-badge kk-badge--cap">{{ avtaleKindLabel(l.kind) }}</span>
      <span class="kk-avtale-desc">{{ l.description }}</span>
      <span class="kk-avtale-amount">{{ l.amountExVat | number:'1.2-2' }} kr</span>
      <span class="kk-avtale-meta">
        {{ intervalLabel(l.interval) }} · from {{ l.startMonth }}<ng-container *ngIf="l.endMonth"> to {{ l.endMonth }}</ng-container>
      </span>
      <button mat-icon-button class="kk-icon-sm" (click)="toggleAvtale(l)" [disabled]="avtaleSaving[l.dbid]"
              [matTooltip]="l.active === false ? 'Resume' : 'Pause — stops appearing on future invoices'">
        <mat-icon>{{ l.active === false ? 'play_arrow' : 'pause' }}</mat-icon>
      </button>
      <button mat-icon-button class="kk-icon-sm" (click)="removeAvtale(l)" [disabled]="avtaleSaving[l.dbid]" matTooltip="Delete">
        <mat-icon>delete_outline</mat-icon>
      </button>
    </div>

    <button mat-button color="primary" class="kk-btn-xs" *ngIf="!avtaleOpen[s.avtaleKey]" (click)="startAvtale(s)">
      <mat-icon>add</mat-icon> Add rent / driftstøtte / service
    </button>

    <div class="kk-avtale-form" *ngIf="avtaleOpen[s.avtaleKey] && avtaleDraft[s.avtaleKey]">
      <label class="kk-f kk-avtale-kind">Type
        <select class="custom-select custom-select-sm" [(ngModel)]="avtaleDraft[s.avtaleKey].kind" (ngModelChange)="onAvtaleKindChange(s)">
          <option *ngFor="let k of avtaleKinds" [ngValue]="k.kind">{{ k.label }}</option>
        </select>
      </label>
      <label class="kk-f kk-avtale-grow">Description (printed on the invoice)
        <input class="form-control form-control-sm" [(ngModel)]="avtaleDraft[s.avtaleKey].description" placeholder="Leie friggkantine per måned">
      </label>
      <label class="kk-f kk-avtale-kind">Amount ex. VAT
        <input type="number" class="form-control form-control-sm" [(ngModel)]="avtaleDraft[s.avtaleKey].amountExVat" placeholder="10000">
      </label>
      <label class="kk-f kk-avtale-kind">Interval
        <select class="custom-select custom-select-sm" [(ngModel)]="avtaleDraft[s.avtaleKey].interval">
          <option value="monthly">Monthly</option>
          <option value="quarterly">Quarterly</option>
          <option value="yearly">Yearly</option>
        </select>
      </label>
      <label class="kk-f kk-avtale-kind">First month
        <input class="form-control form-control-sm" [(ngModel)]="avtaleDraft[s.avtaleKey].startMonth" placeholder="2026-09">
      </label>
      <label class="kk-f kk-avtale-kind">Last month
        <input class="form-control form-control-sm" [(ngModel)]="avtaleDraft[s.avtaleKey].endMonth" placeholder="blank = ongoing">
      </label>
      <div class="kk-avtale-actions">
        <button mat-flat-button color="primary" (click)="saveAvtale(s)" [disabled]="avtaleSaving[s.avtaleKey]">Save</button>
        <button mat-button (click)="cancelAvtale(s)">Cancel</button>
      </div>
      <div class="kk-avtale-error" *ngIf="avtaleError[s.avtaleKey]">{{ avtaleError[s.avtaleKey] }}</div>
      <div class="kk-hint kk-f--full">Don't add the 59 kr administrasjonsgebyr here — it is added automatically to every invoice.</div>
    </div>
  </div>
</ng-template>
